/**
 * Lightweight global store for the Creative Workbench's active persona.
 *
 * CreativeWorkbenchContext writes here whenever its personaId changes so that
 * components outside the provider tree (e.g. TopBar) can read the value
 * without needing access to the React context.
 */
import { create } from 'zustand';

/** Shared with `CreativeWorkbenchContext` localStorage persistence. */
export const CREATIVE_WORKBENCH_STORAGE_KEY = 'creativeWorkbench.lastContext';

interface WorkbenchStore {
  personaId: string | null;
  setPersonaId: (id: string | null) => void;
}

function readPersistedPersonaId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CREATIVE_WORKBENCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { personaId?: string | null };
    return parsed.personaId ?? null;
  } catch {
    return null;
  }
}

/** Clears in-memory persona and drops `personaId` from persisted workbench context (TopBar chip, QA reset). */
export function clearPersistedWorkbenchPersona(): void {
  useWorkbenchStore.getState().setPersonaId(null);
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(CREATIVE_WORKBENCH_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    parsed.personaId = null;
    window.localStorage.setItem(CREATIVE_WORKBENCH_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Quota / private mode — store still cleared above.
  }
}

export const useWorkbenchStore = create<WorkbenchStore>((set) => ({
  personaId: readPersistedPersonaId(),
  setPersonaId: (id) => set({ personaId: id }),
}));
